// Funnel analytics — measures landing → Hub → first generation drop-off
// and time-to-first-output. Fire-and-forget: tracker never blocks UI and
// never throws. Anonymous-friendly via a localStorage browser id; once
// the user signs in their existing browser id stays so we can stitch the
// pre-signup → post-signup journey together.
//
// Server enforces validation in the report_funnel_event RPC. This module
// is the single touchpoint — call `trackFunnel(step, metadata?)` from
// anywhere in the app.

import { supabase } from "@/integrations/supabase/client";

export type FunnelStep =
  | "landing_view"
  | "hub_view"
  | "generation_start"
  | "generation_output"
  // Monetization milestones — measured separately from the acquisition
  // funnel but reported through the same RPC so the admin dashboard can
  // render the full Landing → Upgrade-Click chart in one query.
  | "level_one_complete"
  | "upgrade_click"
  // Soft-intent signals that precede a hard `upgrade_click`. Use this for
  // moments where a user explored pricing/limits without yet committing —
  // e.g. opening the "See full limits" modal on /about. Always tag the
  // surface in metadata so the admin dashboard can split sources.
  | "pricing_interest"
  // Discovery handoff — fired when someone clicks one of the home page's
  // /directory deep-links (per-journey card, "browse all" fallback, the
  // doorway link inside Today's Learning Challenge, etc.). Always tag the
  // surface in metadata (`source`, `journey`) so we can compare which
  // discovery hooks actually convert into directory browsing.
  | "directory_handoff_click"
  // Forward-momentum click — fired by NextStepRail (bottom-of-page) and
  // NavNextChip (top nav) whenever a user moves to the next stage of the
  // Path to AI Mastery. Always tag `surface` ("rail" | "nav_chip"),
  // `step_id` (e.g. "next-stack"), and `open_auth` so we can measure
  // which surface drives more signups vs continuations.
  | "next_step_click"
  // Daily-challenge stall analytics — single funnel step with a
  // metadata.event subtype so we can measure where users drop off in
  // the daily challenge flow (saw the card, opened the walkthrough,
  // toggled a goal, asked for a hint, copied the template, clicked
  // out). Use the `trackDailyChallenge` helper below — it stamps the
  // `event` field and a per-tab dedupe key for once-per-session events
  // like `viewed`.
  | "daily_challenge_event"
  // Hero search live-results analytics. Sub-events live in metadata.event
  // (shown | click | no_results) — see trackHeroSearch helper below.
  | "hero_search_event"
  // Hero A/B framing — `pot` (live hackathon prize) vs `promise`
  // (60-second first-win). metadata.event = 'view' | 'click',
  // metadata.variant = 'pot' | 'promise'.
  | "hero_variant_event";

// Hero search analytics — tracks the live-results panel under the home
// page search bar. Single funnel step with a metadata.event subtype:
//   'shown'      — at least one curated result rendered for this query
//   'click'      — user clicked a result row (metadata.kind = 'playbook' | 'skill')
//   'no_results' — query returned zero curated matches
// metadata.query is always the lowercase trimmed query so the admin
// dashboard can group by query string.
export type HeroSearchEvent = "shown" | "click" | "no_results";

export function trackHeroSearch(
  event: HeroSearchEvent,
  query: string,
  extra: Record<string, unknown> = {},
): Promise<void> {
  const q = (query ?? "").trim().toLowerCase().slice(0, 120);
  if (!q) return Promise.resolve();
  // shown + no_results dedupe per query so we count one impression per
  // typed query, not one per keystroke (debouncedQuery already throttles
  // to ~150ms but the same final query can re-fire on remount). Clicks
  // never dedupe — every click is a real signal.
  const dedupeKey =
    event === "click"
      ? `hero_search:click:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`
      : `hero_search:${event}:${q}`;
  return trackFunnel("hero_search_event", {
    ...extra,
    event,
    query: q,
    dedupeKey,
  });
}

// Sub-events for the daily challenge funnel. Kept narrow on purpose —
// every value here corresponds to a distinct stall point we want to be
// able to read off the admin dashboard. If you add a new event, also
// add a label in DailyChallengeFunnelWidget so it renders nicely.
export type DailyChallengeEvent =
  | "viewed"               // Card mounted on /home (top of funnel)
  | "walkthrough_opened"   // Bloom's-taxonomy walkthrough expanded
  | "goal_toggled"         // User checked or unchecked a goal
  | "hint_requested"       // /hint slash-command invoked from chat
  | "template_copied"      // "Apply this" template copied to clipboard
  | "outbound_click";      // Clicked a /directory or /build handoff

/**
 * Convenience wrapper for the daily challenge sub-funnel. Stamps the
 * sub-event into metadata and emits a per-event dedupe key so React
 * StrictMode double-mounts don't double-count `viewed`. Pass extra
 * metadata (hack_id, surface, etc.) — never PII.
 */
export function trackDailyChallenge(
  event: DailyChallengeEvent,
  metadata: Record<string, unknown> = {},
): Promise<void> {
  // `viewed` is a once-per-tab signal; everything else can repeat
  // (a user toggling 3 goals should record 3 events). We achieve the
  // dedupe by reusing trackFunnel's existing `dedupeKey` mechanism.
  const dedupeKey =
    event === "viewed"
      ? `daily_challenge:viewed:${(metadata.hack_id as string) ?? "any"}`
      : `daily_challenge:${event}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
  return trackFunnel("daily_challenge_event", {
    ...metadata,
    event,
    dedupeKey,
  });
}

const BROWSER_ID_KEY = "hl_funnel_browser_id";
const SESSION_START_KEY = "hl_funnel_session_started_at";
// Per-tab dedupe so an obsessive React StrictMode double-render or a
// spammy useEffect doesn't flood the table.
const sentThisSession = new Set<string>();

export function getBrowserId(): string {
  if (typeof window === "undefined") return "ssr_noop_id_xxxxxx";
  let id = localStorage.getItem(BROWSER_ID_KEY);
  if (!id) {
    // Crypto-strong UUID where available, fallback for ancient browsers.
    id = (crypto as Crypto & { randomUUID?: () => string }).randomUUID
      ? crypto.randomUUID()
      : `b_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(BROWSER_ID_KEY, id);
  }
  return id;
}

function getSessionStart(): string {
  if (typeof window === "undefined") return new Date().toISOString();
  let s = sessionStorage.getItem(SESSION_START_KEY);
  if (!s) {
    s = new Date().toISOString();
    sessionStorage.setItem(SESSION_START_KEY, s);
  }
  return s;
}

/**
 * Record a funnel event. Idempotent per tab+step (so React re-mounts
 * don't double-count landing/hub views). For generation events, pass a
 * unique `dedupeKey` in metadata if you want stricter de-dup.
 */
export async function trackFunnel(
  step: FunnelStep,
  metadata: Record<string, unknown> = {},
): Promise<void> {
  try {
    // Only dedupe view-style events; generation events should always fire.
    // `level_one_complete` is also a once-per-browser milestone — we never
    // want to count the same player crossing the threshold twice in a tab.
    const isOncePerSession =
      step === "landing_view" ||
      step === "hub_view" ||
      step === "level_one_complete";
    const dedupeKey = isOncePerSession
      ? step
      : `${step}:${(metadata.dedupeKey as string) ?? Date.now()}`;
    if (sentThisSession.has(dedupeKey)) return;
    sentThisSession.add(dedupeKey);

    const browserId = getBrowserId();
    const sessionStartedAt = getSessionStart();
    const url = typeof window !== "undefined" ? window.location.pathname + window.location.search : "";
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";

    await supabase.rpc("report_funnel_event", {
      _step: step,
      _browser_id: browserId,
      _session_started_at: sessionStartedAt,
      // Generated Json type is strict/recursive; metadata shape is validated
      // server-side, so cast at the boundary.
      _metadata: metadata as never,
      _url: url,
      _user_agent: ua,
    });
  } catch (err) {
    // Never let analytics break the app.
    if (typeof console !== "undefined") console.warn("[funnel] tracking failed:", err);
  }
}
